/* ═══════════════════════════════════════════════════
   AUTH CONTEXT — Provides login state to entire app
   
   File: src/utils/AuthContext.js
   
   This wraps the entire app so any component can check:
   - Is the user logged in?
   - What's their name/email/uid?
   - Show login/logout buttons accordingly
═══════════════════════════════════════════════════ */

import React, { createContext, useContext, useState, useEffect } from "react";
import { onAuthChange } from "./firebase";

// Create the context
const AuthContext = createContext(null);

/**
 * AuthProvider wraps the app and provides user state everywhere.
 *
 * Usage in App.js:
 *   <AuthProvider>
 *     <Router>...</Router>
 *   </AuthProvider>
 *
 * Usage in any component:
 *   const { user, loading } = useAuth();
 *   if (user) { ... logged in ... }
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen to Firebase auth state changes
    const unsubscribe = onAuthChange((firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || "Student",
        });
      } else {
        // User is signed out
        setUser(null);
      }
      setLoading(false);
    });

    // Cleanup listener on unmount
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to access auth state from any component.
 *
 * Example:
 *   const { user, loading } = useAuth();
 *   if (loading) return <p>Loading...</p>;
 *   if (!user) return <Navigate to="/login" />;
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return context;
}
